import React, { useState, useEffect } from 'react';
import { Box, Alert, Typography, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/auth';

/**
 * FeatureGuard Component
 * 
 * This component checks if the user has access to a specific feature
 * and either renders the protected content or shows an upgrade prompt.
 * 
 * Usage:
 * <FeatureGuard feature="Appointment Booking">
 *   <AppointmentComponent />
 * </FeatureGuard>
 * 
 * Props:
 * - feature: string - The feature name to check (must match database)
 * - category: string - The feature category (optional, for broader access)
 * - fallback: ReactNode - Custom fallback content (optional)
 * - showUpgradeButton: boolean - Whether to show upgrade button (default: true)
 * - children: ReactNode - The content to render if feature is available
 */
const FeatureGuard = ({ 
  feature, 
  category, 
  fallback, 
  showUpgradeButton = true, 
  children 
}) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Always fetch fresh data from API to ensure we have the latest features
        const response = await authService.getCurrentUser();
        console.log("loadUserData", response);
        if (response.success) {
          const currentUser = response.data;
          // Update session storage with fresh data
          if (currentUser.features) {
            sessionStorage.setItem('userFeatures', JSON.stringify(currentUser.features));
          }
          
          setUser(currentUser);
        } else {
          // Fallback to cached data
          const currentUser = authService.getCompleteUser();
          setUser(currentUser);
        }
      } catch (error) {
        // Fallback to cached data
        const currentUser = authService.getCompleteUser();
        setUser(currentUser);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserData();
  }, []);

  // Check if user has access to the specific feature
  const hasFeatureAccess = () => {
    if (!user || !user.features || !user.features.available_features) {
      return false;
    }

    // Check for specific feature
    if (feature) {
      return user.features.available_features.some(
        f => f.name === feature
      );
    }

    // Check for category access
    if (category) {
      return user.features.grouped_features[category] && 
             user.features.grouped_features[category].length > 0;
    }

    return false;
  };

  // Get feature limits if available
  const getFeatureLimit = () => {
    if (!user || !user.features || !user.features.available_features) {
      return null;
    }

    const featureData = user.features.available_features.find(
      f => f.name === feature
    );

    return featureData?.limit_value || null;
  };

  // Check if user is approaching or exceeding limits
  const checkFeatureLimits = () => {
    const limit = getFeatureLimit();
    if (!limit) return { status: 'no-limit', message: null };

    // This would need to be implemented based on actual usage data
    // For now, we'll just return the limit info
    return { 
      status: 'within-limit', 
      message: `Limit: ${limit}`,
      limit 
    };
  };

  // Show loading state while user data is being fetched
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Box>
    );
  }

  // If user has access, render the children
  if (hasFeatureAccess()) {
    const limitInfo = checkFeatureLimits();
    
    return (
      <Box>
        {children}
        {limitInfo.message && (
          <Box sx={{ mt: 1, textAlign: 'right' }}>
            <Typography variant="caption" color="textSecondary">
              {limitInfo.message}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // If user doesn't have access, show fallback or upgrade prompt
  if (fallback) {
    return fallback;
  }

  // Default upgrade prompt
  return (
    <Box sx={{ p: 3, textAlign: 'center' }}>
      <Alert 
        severity="info" 
        sx={{ mb: 2 }}
      >
        <Typography variant="body1" sx={{ mb: 1 }}>
          <strong>{feature || category}</strong> is not available in your current plan.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Current Plan: <strong>{user?.features?.plan_name || 'Unknown'}</strong>
          <br />
          Please contact support to upgrade your plan.
        </Typography>
      </Alert>
    </Box>
  );
};

/**
 * useFeatureAccess Hook
 * 
 * Custom hook to check feature access in components
 * 
 * Usage:
 * const { hasAccess, featureLimit, planName } = useFeatureAccess('Appointment Booking');
 */
export const useFeatureAccess = (featureName) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Always fetch fresh data from API to ensure we have the latest features
        const response = await authService.getCurrentUser();
        
        if (response.success) {
          const currentUser = response.data;
          // Update session storage with fresh data
          if (currentUser.features) {
            sessionStorage.setItem('userFeatures', JSON.stringify(currentUser.features));
          }
          
          setUser(currentUser);
        } else {
          // Fallback to cached data
          setUser(authService.getCompleteUser());
        }
      } catch (error) {
        // Fallback to cached data
        setUser(authService.getCompleteUser());
      }
    };
    
    loadUserData();
  }, []);

  const hasAccess = () => {
    if (!user || !user.features || !user.features.available_features) {
      return false;
    }

    return user.features.available_features.some(
      f => f.name === featureName && f.is_included
    );
  };

  const getFeatureLimit = () => {
    if (!user || !user.features || !user.features.available_features) {
      return null;
    }

    const featureData = user.features.available_features.find(
      f => f.name === featureName
    );

    return featureData?.limit_value || null;
  };

  const hasCategoryAccess = (category) => {
    if (!user || !user.features || !user.features.grouped_features) {
      return false;
    }

    return user.features.grouped_features[category] && 
           user.features.grouped_features[category].length > 0;
  };

  return {
    hasAccess: hasAccess(),
    featureLimit: getFeatureLimit(),
    planName: user?.features?.plan_name || 'Unknown',
    hasCategoryAccess: hasCategoryAccess,
    allFeatures: user?.features?.available_features || [],
    groupedFeatures: user?.features?.grouped_features || {}
  };
};

export default FeatureGuard;
